import { useState } from 'react';
import { ApiError, api, type Framework } from '../api.ts';
import { entitledTo } from '../billing.ts';
import { message } from './format.ts';

interface FrameworksCardProps {
  tenantId: string;
  /** The catalogue's frameworks, in report order. */
  catalogue: readonly Framework[];
  /** What this account's package covers, or null for no restriction. */
  allowed: readonly string[] | null;
  /**
   * What this client's own plan entitles it to, or null when the plan does not
   * restrict it (no billing, or a client from before it). Applied on top of
   * the package: a framework has to pass both.
   */
  entitled: readonly string[] | null;
  /** As stored: null for a client nobody has chosen for. */
  selected: readonly string[] | null;
  /**
   * What was saved, so the screen around this card can follow without asking
   * the server again. The selection decides which framework criteria every
   * finding shows, and there is no per-tenant GET to re-read: the card is the
   * only thing that knows, at the moment it knows it.
   */
  onSaved: (frameworks: string[] | null) => void;
  onError: (message: string) => void;
  /** Takes the operator to this client's Plan tab, where a framework is added. */
  onOpenPlan: () => void;
}

/**
 * The server's refusal of a framework outside this client's plan, or null for
 * any other failure. Checked by shape, because the console cannot assume which
 * version of the API it is talking to.
 */
const planRefusal = (cause: unknown): string | null => {
  if (!(cause instanceof ApiError) || cause.status !== 409) return null;
  const body = cause.body as { error?: unknown } | undefined;
  return typeof body === 'object' && body !== null && body.error === 'allowance_exceeded'
    ? cause.message
    : null;
};

/**
 * Which frameworks this client's reports are projected against.
 *
 * Every control is still evaluated for every client whatever is ticked here.
 * This decides which projections of those verdicts the report prints, and
 * nothing else — the card says so out loud, because a screen full of framework
 * checkboxes on a compliance product invites the reading that unticking one
 * stops something being checked.
 *
 * "Follow the catalogue" is a state of its own rather than the all-ticked case.
 * A client on it picks up an eighth framework when we add one; a client who has
 * ticked all seven has made a decision about seven, and quietly widening that
 * decision on their behalf is how a report grows a section its owner never
 * agreed to. The two are one radio apart here and one `null` apart in the
 * database.
 *
 * **A package can cover fewer than the catalogue does, and then this card shows
 * only those.** Not as enforcement — `PUT /tenants/:id/frameworks` refuses
 * anything outside the package, and that is the control. It is so that an
 * operator is not offered a choice the server will refuse after they have made
 * it.
 *
 * "Follow" is offered under a package too, and there it means every framework
 * the package covers, now and later. It was hidden until 2026-09-20, when the
 * route refused `null` under a restriction — which left a client who had ever
 * ticked a box unable to return to it at all.
 *
 * **A client's own plan narrows it again.** Unlike the package, the plan is
 * something an owner changes in the console, so the frameworks it leaves out
 * are shown disabled with the way to add them, rather than hidden. The server
 * refuses them with a 409 either way; that refusal is shown beside Save, with
 * the same way out, because it means the plan changed under this card rather
 * than that anything is broken.
 */
export const FrameworksCard = ({
  tenantId,
  catalogue,
  allowed,
  entitled,
  selected,
  onSaved,
  onError,
  onOpenPlan,
}: FrameworksCardProps) => {
  const packaged = (id: string): boolean => allowed === null || allowed.includes(id);
  const covers = (id: string): boolean => packaged(id) && entitledTo(entitled, id);
  /** The catalogue narrowed to the package and the plan. The order stays the catalogue's. */
  const offered = catalogue.filter((framework) => covers(framework.id));
  /*
   * Shown, disabled, rather than hidden like a framework the package excludes.
   * The package is a conversation with us; the plan is something an owner can
   * change two tabs along, and a framework nobody can see is one nobody thinks
   * to add.
   */
  const outsidePlan = catalogue.filter(
    (framework) => packaged(framework.id) && !entitledTo(entitled, framework.id),
  );

  /*
   * A stored selection can name a framework the package no longer covers — the
   * package was narrowed after the client chose. Those are dropped from the
   * report already (the pipeline intersects at render), so the card drops them
   * too rather than carrying an invisible tick into a save the server would
   * refuse. They are named below instead.
   */
  // Stored `null` means "follow", under a package or without one.
  const [following, setFollowing] = useState(selected === null);
  const [ticked, setTicked] = useState<readonly string[]>(
    (selected ?? offered.map((framework) => framework.id)).filter(covers),
  );
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  /** The server's 409 for a framework outside the plan, kept beside Save rather than in the banner. */
  const [refused, setRefused] = useState<string | null>(null);

  const dropped = (selected ?? []).filter((id) => !covers(id));
  const nameOf = (id: string): string =>
    catalogue.find((framework) => framework.id === id)?.name ?? id;

  const toggle = (id: string) =>
    setTicked((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );

  const empty = !following && ticked.length === 0;

  const save = async () => {
    setSaving(true);
    setStatus(null);
    setRefused(null);
    try {
      // One value, saved and reported upward, so the screen cannot end up
      // showing a selection different from the one that was stored.
      const saved = following ? null : [...ticked];
      await api.putTenantFrameworks(tenantId, saved);
      setStatus(
        following
          ? allowed === null && entitled === null
            ? 'Saved. This client’s reports cover every framework in the catalogue, including any added later.'
            : 'Saved. This client’s reports cover every framework they are entitled to, and will widen if that does.'
          : 'Saved. The next assessment will report against the frameworks ticked above.',
      );
      onSaved(saved);
    } catch (cause) {
      /*
       * The plan changed under this card — in another tab, or by another
       * owner — so a tick it offered is no longer allowed. That is not a
       * failure of the screen, and it has a remedy one click away.
       */
      const refusal = planRefusal(cause);
      if (refusal === null) onError(message(cause));
      else setRefused(refusal);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <div className="card-head">
        <h2>Framework coverage</h2>
      </div>
      <div className="card-body">
        <p className="lead td-lead">
          Every control is assessed for this client whatever you choose here. This decides which
          frameworks their report is projected against — an accountancy practice pursuing Cyber
          Essentials does not need thirteen ISO 27001 Annex A controls in front of its assessor.
        </p>

        {/*
          Said once, plainly, rather than left for somebody to infer from a
          short list. An operator who knows the catalogue covers seven and sees
          two here should be told why, and told that it is a package rather than
          a fault.
        */}
        {allowed !== null && entitled === null && (
          <p className="hint td-lead">
            Your package covers {offered.length === 1 ? 'one framework' : `${offered.length} frameworks`}, so
            these are the ones this client’s reports can be projected against. Changing that is a
            change of package.
          </p>
        )}
        {entitled !== null && (
          <p className="hint td-lead">
            This client’s plan covers{' '}
            {offered.length === 1 ? 'one framework' : `${offered.length} frameworks`}, so these are
            the ones their reports can be projected against.
          </p>
        )}

        {dropped.length > 0 && (
          <div className="callout callout-caution">
            <span className="callout-title">
              {dropped.length === 1 ? 'A framework chosen earlier is' : 'Frameworks chosen earlier are'} outside
              this client’s plan or package
            </span>
            {dropped.map(nameOf).join(', ')} {dropped.length === 1 ? 'was' : 'were'} chosen for this
            client and {dropped.length === 1 ? 'is' : 'are'} no longer covered, so{' '}
            {dropped.length === 1 ? 'it has' : 'they have'} already stopped appearing in their
            reports. Saving here drops {dropped.length === 1 ? 'it' : 'them'} from the stored
            selection as well.
          </div>
        )}

        {/*
          The first option says something different under a package, because it
          means something different: the package's list rather than the
          catalogue's. Both are stored as `null` — "whatever this client may
          have" — and the wording is what tells an operator which they are
          choosing.
        */}
        <div className="field td-check">
          <label htmlFor="fw-follow">
            <input
              id="fw-follow"
              type="radio"
              name="fw-mode"
              checked={following}
              onChange={() => setFollowing(true)}
            />
            {allowed === null && entitled === null
              ? 'Every framework, including any we add later'
              : entitled !== null
                ? 'Every framework this client’s plan covers, including any added to it later'
                : 'Every framework this package covers, including any added to it later'}
          </label>
        </div>

        <div className="field td-check">
          <label htmlFor="fw-choose">
            <input
              id="fw-choose"
              type="radio"
              name="fw-mode"
              checked={!following}
              onChange={() => setFollowing(false)}
            />
            Only the frameworks I choose
          </label>
        </div>

        <fieldset className="td-frameworks" disabled={following}>
          <legend className="hint">
            Order in the report is the catalogue’s, not the order you tick them.
          </legend>
          {offered.map((framework) => (
            <div className="field td-check" key={framework.id}>
              <label htmlFor={`fw-${framework.id}`}>
                <input
                  id={`fw-${framework.id}`}
                  type="checkbox"
                  checked={ticked.includes(framework.id)}
                  onChange={() => toggle(framework.id)}
                />
                {framework.name}
              </label>
              <span className="hint">{framework.version}</span>
            </div>
          ))}
          {outsidePlan.map((framework) => (
            <div className="field td-check" key={framework.id}>
              <label htmlFor={`fw-${framework.id}`}>
                <input
                  id={`fw-${framework.id}`}
                  type="checkbox"
                  checked={false}
                  disabled
                  aria-describedby="fw-outside-plan"
                  onChange={() => undefined}
                />
                {framework.name}
              </label>
              <span className="hint">Not in this client’s plan</span>
            </div>
          ))}
        </fieldset>

        {outsidePlan.length > 0 && (
          <p className="hint td-lead" id="fw-outside-plan">
            Frameworks outside this client’s plan cannot be chosen here.{' '}
            <button type="button" className="link" onClick={onOpenPlan}>
              Change this client’s plan
            </button>
          </p>
        )}

        <div className="toolbar">
          <button
            type="button"
            className="btn btn-primary"
            aria-busy={saving}
            disabled={saving || empty}
            onClick={() => void save()}
          >
            {saving ? 'Saving…' : 'Save coverage'}
          </button>
          <span className="status-inline" role="status">
            {empty
              ? 'Choose at least one framework, or switch to every framework.'
              : (refused ?? status)}
          </span>
          {refused !== null && (
            <button type="button" className="link" onClick={onOpenPlan}>
              Open this client’s plan
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
